// src/WidgetPage.tsx
import { useEffect, useRef } from "react";
import { WidgetShell } from "./components/WidgetShell";
import { WidgetContent } from "./components/widget-content";
import { useStudioStore } from "./store/studio-store";
import type { WidgetState } from "./store/studio-store";
import { useAuthStore } from "./store/auth-store";
import { useViewStore } from "./store/view-store";
import { api } from "./lib/api";
import { track } from "./lib/analytics";
import { readPendingCheckout } from "./lib/pending-checkout";
import { getWidgetLocale } from "./lib/widget-locale";
import { ReturnToStudio } from "./components/return-to-studio";
import { WidgetSkeleton } from "./components/widget-skeleton";
import {
  BookingProvider,
  deriveStudioTheme,
  normalizeHex,
  studioThemeVars,
  toBookingLocale,
} from "@studiolite/booking";
import i18n, { useLocale } from "../i18n";
import { WidgetLink } from "./lib/booking-link";

/**
 * data-font="inherit" : la pile de polices lue sur la page hote. Elle finit
 * dans une propriete CSS, on n'y laisse donc que des noms de polices.
 */
function sanitizeFontFamily(value: string | null): string | null {
  if (!value) return null;
  const clean = value.replace(/[^\p{L}\p{N} ,'"_-]/gu, "").trim();
  return clean || null;
}

export function WidgetPage() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const sendHeight = () => {
      if (!containerRef.current) return;
      const height = containerRef.current.scrollHeight;
      window.parent.postMessage({ type: "WIDGET_RESIZE", height }, "*");
    };

    sendHeight();

    const observer = new ResizeObserver(sendHeight);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  const params = new URLSearchParams(window.location.search);
  const studioId = params.get("studioId");
  const layout = params.get("layout");
  const primary = params.get("primary");
  const background = params.get("background");
  const font = params.get("font");
  const hasMaxHeight = params.has("maxHeight");
  const paymentReturn = params.get("payment_return") === "true";
  const returnedBooking = params.get("bz_booking");
  // Stripe est sorti de l'iframe : le widget s'ouvre seul, en pleine page.
  const topLevelReturn = !!returnedBooking && window.parent === window;

  const setStudioId = useStudioStore((s: WidgetState) => s.setStudioId);
  const loadStudio = useStudioStore((s: WidgetState) => s.loadStudio);
  const status = useStudioStore((s: WidgetState) => s.status);
  const studio = useStudioStore((s: WidgetState) => s.studio);

  const locale = useLocale();
  const { login } = useAuthStore();
  const { navigate, onBookingSuccess } = useViewStore();

  useEffect(() => {
    const root = document.documentElement;

    if (primary) {
      root.style.setProperty(`--color-primary`, primary);
    }

    if (background) {
      root.style.setProperty("--widget-bg", background);
      document.body.style.backgroundColor = background;
    }

    const family = sanitizeFontFamily(font);
    if (family) root.style.setProperty("--bz-font-host", family);

    // data-height : l'iframe est plafonnee par la page hote, le widget defile
    // alors dans son cadre (index.css masque le debordement sinon).
    root.classList.toggle("bz-capped", hasMaxHeight);
  }, [primary, background, font, hasMaxHeight]);

  // Couleur : data-primary, sinon celle du studio. Les teintes derivees
  // (accent-soft, texte sur accent) sont calculees comme sur le site du
  // studio, par le meme deriveStudioTheme.
  useEffect(() => {
    const color = normalizeHex(primary) ?? normalizeHex(studio?.primaryColor);
    if (!color) return;

    const root = document.documentElement;
    for (const [name, value] of Object.entries(
      studioThemeVars(deriveStudioTheme(color)),
    )) {
      root.style.setProperty(name, value);
    }
    // Les ecrans actuels lisent encore --color-primary.
    if (!primary) root.style.setProperty("--color-primary", color);
  }, [primary, studio?.primaryColor]);

  // Langue resolue au chargement du studio (lib/widget-locale) : data-locale,
  // sinon le choix de l'admin, sinon la langue du studio.
  useEffect(() => {
    if (!studio) return;
    const resolved = getWidgetLocale();
    if (resolved) i18n.changeLanguage(toBookingLocale(resolved));
  }, [studio]);

  useEffect(() => {
    if (status === "ready") track("widget_loaded", { layout });
  }, [status, layout]);

  useEffect(() => {
    if (!studioId) return;
    setStudioId(studioId);
    loadStudio(studioId);
  }, [studioId, loadStudio, setStudioId]);

  // Partie de l'iframe visible a l'ecran (embed.js, « bookzen:viewport ») :
  // les fenetres de reservation s'y placent au lieu du milieu d'une iframe de
  // plusieurs milliers de pixels (index.css, html.bz-viewport).
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent || event.data?.type !== "bookzen:viewport") return;
      const top = Number(event.data.top);
      const height = Number(event.data.height);
      if (!Number.isFinite(top) || !Number.isFinite(height) || height <= 0) return;
      const root = document.documentElement;
      root.style.setProperty("--bz-vp-top", `${Math.max(0, top)}px`);
      root.style.setProperty("--bz-vp-height", `${height}px`);
      root.classList.add("bz-viewport");
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    if (returnedBooking && !topLevelReturn) {
      // De retour de Stripe dans l'iframe, ou via #bz-booking= sur la page du
      // studio : la confirmation. Le parametre disparait, un rechargement ne
      // la remontre pas.
      onBookingSuccess(returnedBooking);
      const cleaned = new URLSearchParams(window.location.search);
      cleaned.delete("bz_booking");
      history.replaceState(null, "", `${window.location.pathname}?${cleaned.toString()}`);
      return;
    }

    // Le site a perdu l'ancre de retour (ou un ancien embed.js en cache ne la
    // lit pas) : un paiement recent, confirme depuis, ouvre sa confirmation.
    const pending = readPendingCheckout();
    if (pending && useAuthStore.getState().token) {
      api(`/api/widget/bookings/${pending.bookingId}`, { skipUnauthorizedLogout: true })
        .then((booking: { status?: string }) => {
          if (booking?.status === "CONFIRMED") onBookingSuccess(pending.bookingId);
        })
        .catch(() => {});
    }

    if (paymentReturn) {
      navigate("profile");
      const cleaned = new URLSearchParams(window.location.search);
      cleaned.delete("payment_return");
      const newSearch = cleaned.toString();
      history.replaceState(
        null,
        "",
        window.location.pathname + (newSearch ? `?${newSearch}` : ""),
      );
    } else if (layout === "packages") {
      navigate("packages");
    } else if (layout === "private-booking") {
      navigate("private-booking");
    } else if (layout === "videos") {
      navigate("videos");
    }
  }, [layout, navigate, paymentReturn, returnedBooking, topLevelReturn, onBookingSuccess]);

  useEffect(() => {
    const initialUserId = useAuthStore.getState().user?.id ?? null;
    const initialToken = useAuthStore.getState().token;

    api("/api/widget/auth/me", { skipUnauthorizedLogout: true })
      .then((session) =>
        login({
          user: session.user,
          token: session.token,
        }),
      )
      .catch(() => {
        const currentAuth = useAuthStore.getState();
        const currentUserId = currentAuth.user?.id ?? null;

        if (
          currentUserId === initialUserId &&
          currentAuth.token === initialToken
        ) {
          currentAuth.logout();
        }
      });
  }, [login]);

  // Le conteneur reste monte pour toute la vie du composant : c'est lui que
  // le ResizeObserver observe (effet ci-dessus, deps []), et le detacher pour
  // chaque etat idle/loading/error faisait perdre le suivi de hauteur des la
  // premiere transition, et plantait sur iOS Safari quand un callback en vol
  // visait un noeud deja retire (cf. BOOKZEN-WIDGET-UI-1).
  return (
    <div ref={containerRef}>
      {topLevelReturn ? (
        <ReturnToStudio bookingId={returnedBooking} />
      ) : !studioId ? (
        <div>Missing studio configuration</div>
      ) : status === "loading" || status === "idle" ? (
        <WidgetSkeleton />
      ) : status === "error" ? (
        <div>Error : Studio not found</div>
      ) : (
        <BookingProvider
          locale={locale}
          timeZone={studio?.timezone ?? "Europe/Zurich"}
          currency={studio?.currency ?? "CHF"}
          Link={WidgetLink}
        >
          <WidgetShell>
            <WidgetContent />
          </WidgetShell>
        </BookingProvider>
      )}
    </div>
  );
}
